import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  Button,
  Typography,
  Space,
  InputNumber,
  message,
  Empty,
  Divider,
  Row,
  Col,
} from "antd";
import {
  DeleteOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import API_CONFIG from '../config/api.js';

const { Title, Text } = Typography;

interface CartItem {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  item_total: number;
  image_url?: string;
}

interface ShoppingCartProps {
  visible: boolean;
  onClose: () => void;
  storeSlug: string;
  onCartUpdate: (count: number) => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = ({
  visible,
  onClose,
  storeSlug,
  onCartUpdate,
}) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Hook pour détecter la taille d'écran
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // Mobile breakpoint
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // URLs sécurisées pour APIs publiques uniquement
  const STORE_API = API_CONFIG.PUBLIC_STORE_API;

  useEffect(() => {
    if (visible) {
      fetchCart();
    }
  }, [visible, storeSlug]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) {
        setCartItems([]);
        setTotal(0);
        onCartUpdate(0);
        return;
      }

      const response = await fetch(`${STORE_API}/${storeSlug}/cart`, {
        headers: {
          "X-Session-ID": sessionId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
        setTotal(parseFloat(data.total || 0));

        // ✅ FIX: Check if data and data.items exist before reduce()
        const itemCount = (data && data.items && Array.isArray(data.items))
          ? data.items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0)
          : 0;
        onCartUpdate(itemCount);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(productId);
      return;
    }

    try {
      const sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) return;

      const response = await fetch(
        `${STORE_API}/${storeSlug}/cart/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": sessionId,
          },
          body: JSON.stringify({
            product_id: productId,
            quantity: newQuantity,
            replace: true, // Replace existing quantity
          }),
        }
      );

      if (response.ok) {
        fetchCart();
      } else {
        message.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      message.error("Erreur de connexion");
    }
  };

  const removeItem = async (productId: number) => {
    try {
      const sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) return;

      const response = await fetch(
        `${STORE_API}/${storeSlug}/cart/remove`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": sessionId,
          },
          body: JSON.stringify({
            product_id: productId,
          }),
        }
      );

      if (response.ok) {
        message.success("Produit retiré du panier");
        fetchCart();
      } else {
        message.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error removing item:", error);
      message.error("Erreur de connexion");
    }
  };

  const proceedToCheckout = () => {
    if (cartItems.length === 0) {
      message.warning("Votre panier est vide");
      return;
    }

    // Navigate to checkout
    onClose();
    window.location.href = `/store/${storeSlug}/checkout`;
  };

  return (
    <Drawer
      title={
        <Space>
          <ShoppingCartOutlined />
          {isMobile ? `Panier (${cartItems.length})` : `Mon Panier (${cartItems.length} articles)`}
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={isMobile ? "100%" : 400} // Plein écran sur mobile, 400px sur desktop
      footer={
        cartItems.length > 0 && (
          <div style={{ padding: "1rem 0" }}>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: "1rem" }}
            >
              <Col>
                <Text strong style={{ fontSize: 18 }}>
                  Total:
                </Text>
              </Col>
              <Col>
                <Text strong style={{ fontSize: 18, color: "#52c41a" }}>
                  {new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "XOF",
                  }).format(total)}
                </Text>
              </Col>
            </Row>
            <Button
              type="primary"
              size="large"
              block
              icon={<ShoppingCartOutlined />}
              onClick={proceedToCheckout}
            >
              Commander
            </Button>
          </div>
        )
      }
    >
      {cartItems.length === 0 ? (
        <Empty
          description="Votre panier est vide"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: "3rem 0" }}
        >
          <Button type="primary" onClick={onClose}>
            Continuer les achats
          </Button>
        </Empty>
      ) : (
        <List
          dataSource={cartItems}
          loading={loading}
          renderItem={(item) => (
            <List.Item
              style={{ padding: "1rem 0" }}
              actions={[
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size={isMobile ? "large" : "middle"}
                  onClick={() => removeItem(item.product_id)}
                  style={isMobile ? { padding: "8px 12px" } : undefined}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={
                  item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ShoppingCartOutlined style={{ color: "#ccc" }} />
                    </div>
                  )
                }
                title={
                  <Text strong style={{ fontSize: 14 }}>
                    {item.product_name}
                  </Text>
                }
                description={
                  <div>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <Text type="secondary">
                        Prix unitaire:{" "}
                        {new Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "XOF",
                        }).format(item.unit_price)}
                      </Text>
                    </div>
                    <Space>
                      <Text>Quantité:</Text>
                      <InputNumber
                        min={1}
                        max={999}
                        value={item.quantity}
                        onChange={(value) =>
                          updateQuantity(item.product_id, value || 1)
                        }
                        size={isMobile ? "large" : "small"}
                        style={{ width: isMobile ? 90 : 70 }}
                      />
                    </Space>
                    <div style={{ marginTop: "0.5rem" }}>
                      <Text strong>
                        Sous-total:{" "}
                        {new Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "XOF",
                        }).format(item.item_total)}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default ShoppingCart;
